import type { RepeatingSectionRowData } from '@/features/AI-documents/types/docGen.types';
import { useSessionStore } from '@/shared/stores/sessionStore';
import {
  buildOfficerAppointmentContactDataFromFlat,
  enrichOfficerAppointmentSeedFormData,
} from '../../appoint-director/composables/adaptOfficerAppointmentEfilingFormData';
import {
  applyContactDataToDirectorStructuredProfile,
  formatDirectorStructuredAddress,
} from '../../appoint-director/composables/directorStructuredProfile';
import { nationalityLabelForOutput } from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function normalizeSectionKey(name: unknown): string {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

/** 템플릿 `[[if new_secretaries]]` 구간 — 행 키는 문서 추출 결과를 따른다 */
export function getNewSecretariesRepeatingKey(model: AppointSecretaryFormModel): string | null {
  const rd = model.repeatingData || {};
  const hit = Object.keys(rd).find((k) => /new[_\s-]*secretar(y|ies)/i.test(normalizeSectionKey(k)));
  return hit || null;
}

export function getNewSecretariesRows(model: AppointSecretaryFormModel): RepeatingSectionRowData[] {
  const key = getNewSecretariesRepeatingKey(model);
  if (!key) return [];
  const rows = model.repeatingData?.[key];
  return Array.isArray(rows) ? rows : [];
}

function firstSecretaryNameId(model: AppointSecretaryFormModel): { name: string; id: string } {
  const scalarName = trim(model.secretaryName);
  const scalarId = trim(model.secretaryIdentificationNo);
  if (scalarName || scalarId) {
    return { name: scalarName, id: scalarId };
  }
  const row = getNewSecretariesRows(model)[0] || {};
  const name = trim(
    row.secretary_name ??
      row.director_name ??
      row.name ??
      row.new_secretary_name,
  );
  const idNo = trim(
    row.secretary_identification_no ??
      row.director_identification_no ??
      row.identification_no ??
      row.nric,
  );
  return { name, id: idNo };
}

/**
 * B32103 — 문서 플레이스홀더만 반영
 * 공통: company_name, company_registration_no, secretary_name, secretary_identification_no (반복 첫 행 → 스칼라 동기)
 * DR: resolution_date
 * Form 45B: appointment_effective_date, execution_date, secretary_address, secretary_nationality
 */
export interface AppointSecretaryFormModel {
  companyName: string;
  companyRegistrationNo: string;
  resolutionDate: string;
  appointmentEffectiveDate: string;
  executionDate: string;
  secretaryName: string;
  secretaryIdentificationNo: string;
  secretaryAddress: string;
  secretaryAlternateAddress: string;
  secretaryNationality: string;
  secretaryDateOfBirth: string;
  secretaryEmail: string;
  secretaryContactNo: string;
  secretaryIdTypeCd: string;
  secretaryMobileCountryCd: string;
  secretaryMobileAreaCode: string;
  repeatingData: Record<string, RepeatingSectionRowData[]>;
  contactCd: string;
  positionCd: string;
  contactData: Record<string, any> | null;
}

export function createEmptyAppointSecretaryForm(): AppointSecretaryFormModel {
  return {
    companyName: '',
    companyRegistrationNo: '',
    resolutionDate: '',
    appointmentEffectiveDate: '',
    executionDate: '',
    secretaryName: '',
    secretaryIdentificationNo: '',
    secretaryAddress: '',
    secretaryAlternateAddress: '',
    secretaryNationality: '',
    secretaryDateOfBirth: '',
    secretaryEmail: '',
    secretaryContactNo: '',
    secretaryIdTypeCd: '',
    secretaryMobileCountryCd: '',
    secretaryMobileAreaCode: '',
    repeatingData: {},
    contactCd: '',
    positionCd: 'B30033',
    contactData: null,
  };
}

export function applySecretaryStructuredProfileFromContactData(
  model: AppointSecretaryFormModel,
  contactData: Record<string, unknown> | null | undefined,
): void {
  const profile = applyContactDataToDirectorStructuredProfile(contactData);
  model.secretaryIdTypeCd = profile.idTypeCd;
  model.secretaryMobileCountryCd = profile.mobileCountryCd;
  model.secretaryMobileAreaCode = profile.mobileAreaCode;
  const primaryText = formatDirectorStructuredAddress(profile.primary);
  const altText = formatDirectorStructuredAddress(profile.alternate);
  if (primaryText) model.secretaryAddress = primaryText;
  if (altText) model.secretaryAlternateAddress = altText;
}

function looksLikeUuid(s: string): boolean {
  if (!s) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim());
}

function resolveEntityUenForFiling(model: AppointSecretaryFormModel, entityCd: string): string {
  const companyUen = trim(model.companyRegistrationNo);
  const ec = trim(entityCd);
  if (companyUen) return companyUen;
  if (ec && !looksLikeUuid(ec)) return ec;
  return '';
}

function resolveFilingAgentUenForFiling(): string {
  try {
    const sessionStore = useSessionStore();
    return trim(sessionStore.getCustomerInfo()?.regNo || '');
  } catch {
    return '';
  }
}

function joinForeignAddressLines(cd: Record<string, any> | null | undefined): string {
  if (!cd || typeof cd !== 'object') return '';
  const f1 = trim(cd.FOREIGN_ADDRESS_LINE_1 ?? cd.foreignAddressLine1);
  const f2 = trim(cd.FOREIGN_ADDRESS_LINE_2 ?? cd.foreignAddressLine2);
  if (!f1 && !f2) return '';
  return [f1, f2].filter(Boolean).join(', ').trim();
}

function joinAltForeignAddressLines(cd: Record<string, any> | null | undefined): string {
  if (!cd || typeof cd !== 'object') return '';
  const f1 = trim(cd.ALT_FOREIGN_ADDRESS_LINE_1 ?? cd.altForeignAddressLine1);
  const f2 = trim(cd.ALT_FOREIGN_ADDRESS_LINE_2 ?? cd.altForeignAddressLine2);
  if (!f1 && !f2) return '';
  return [f1, f2].filter(Boolean).join(', ').trim();
}

function normalizeRepeatingData(
  repeatingData: Record<string, RepeatingSectionRowData[]> | null | undefined,
): Record<string, RepeatingSectionRowData[]> {
  const out: Record<string, RepeatingSectionRowData[]> = {};
  if (!repeatingData || typeof repeatingData !== 'object') return out;

  for (const [sectionName, rows] of Object.entries(repeatingData)) {
    if (!Array.isArray(rows)) continue;
    const cleanRows = rows
      .filter((row) => row && typeof row === 'object' && !Array.isArray(row))
      .map((row) => {
        const cleanRow: RepeatingSectionRowData = {};
        for (const [key, value] of Object.entries(row)) {
          cleanRow[key] = trim(value);
        }
        return cleanRow;
      });
    if (cleanRows.length > 0) out[sectionName] = cleanRows;
  }

  return out;
}

export function buildFlatContactSourceFromSecretaryModel(
  model: AppointSecretaryFormModel,
): Record<string, unknown> {
  const { name, id } = firstSecretaryNameId(model);
  return {
    secretaryName: model.secretaryName || name,
    secretary_name: model.secretaryName || name,
    secretaryIdentificationNo: model.secretaryIdentificationNo || id,
    secretary_identification_no: model.secretaryIdentificationNo || id,
    secretaryNationality: model.secretaryNationality,
    secretaryDateOfBirth: model.secretaryDateOfBirth,
    secretaryEmail: model.secretaryEmail,
    secretaryContactNo: model.secretaryContactNo,
    secretaryAddress: model.secretaryAddress,
    secretaryAlternateAddress: model.secretaryAlternateAddress,
    secretaryIdTypeCd: model.secretaryIdTypeCd,
    secretaryMobileCountryCd: model.secretaryMobileCountryCd,
    secretaryMobileAreaCode: model.secretaryMobileAreaCode,
    contactCd: model.contactCd,
    repeatingData: model.repeatingData,
  };
}

/** 수동 입력 → e-Filing contactData (합쳐진 주소 문자열을 역분해) */
export function resolveAppointSecretaryContactData(
  model: AppointSecretaryFormModel,
): Record<string, any> | null {
  const flat = buildFlatContactSourceFromSecretaryModel(model);
  const built = buildOfficerAppointmentContactDataFromFlat(flat) as Record<string, any>;
  if (!trim(built.CONTACT_NAME)) return null;
  const contactCd = trim(model.contactCd);
  if (contactCd) {
    built.contactCd = contactCd;
    built.officerNameCd = contactCd;
  }
  return built;
}

/** Create/Update 직전 formModel.contactData 반영 (presetForm 복원용) */
export function syncAppointSecretaryManualContactData(model: AppointSecretaryFormModel): void {
  model.contactData = resolveAppointSecretaryContactData(model);
}

/** 문서 변수 — 위 플레이스홀더만 (반복 구간은 repeatingData 그대로 전달) */
export function toAppointSecretaryTemplateVariables(model: AppointSecretaryFormModel): Record<string, any> {
  const repeatingData = normalizeRepeatingData(model.repeatingData);
  const { name, id: idNo } = firstSecretaryNameId(model);
  const cd = model.contactData as Record<string, any> | null | undefined;
  const addrType = trim(cd?.ADDRESS_TYPE_CD ?? cd?.addressTypeCd);
  const primaryFj = joinForeignAddressLines(cd);
  const secretaryAddr =
    addrType === 'B07002' && primaryFj ? primaryFj : trim(model.secretaryAddress);

  return {
    company_name: trim(model.companyName),
    company_registration_no: trim(model.companyRegistrationNo),
    secretary_name: name,
    secretary_identification_no: idNo,
    resolution_date: trim(model.resolutionDate),
    appointment_effective_date: trim(model.appointmentEffectiveDate),
    execution_date: trim(model.executionDate),
    secretary_address: secretaryAddr,
    secretary_nationality: nationalityLabelForOutput(trim(model.secretaryNationality)),
    Signers: '',
    signers: '',
    repeatingData,
    ...repeatingData,
  };
}

export function buildAppointSecretaryTaskFormData(
  model: AppointSecretaryFormModel,
  entityCd: string,
): Record<string, any> {
  const contactData = resolveAppointSecretaryContactData(model);
  const contactCd = trim(model.contactCd) || trim(contactData?.contactCd);
  const { name: secName, id: secId } = firstSecretaryNameId(model);
  const officerName =
    secName ||
    trim(contactData?.contactName) ||
    [trim(contactData?.firstName), trim(contactData?.middleName), trim(contactData?.surname)]
      .filter(Boolean)
      .join(' ')
      .trim();
  const appointmentDate = trim(model.appointmentEffectiveDate);
  const entityName = trim(model.companyName);
  const entityRegNo = trim(model.companyRegistrationNo);

  const addrType = trim((contactData as any)?.ADDRESS_TYPE_CD || (contactData as any)?.addressTypeCd);
  const altType = trim((contactData as any)?.ALTERNATE_ADDRESS_CD || (contactData as any)?.alternateAddressCd);
  const primaryForeignJoined = joinForeignAddressLines(contactData);
  const altForeignJoined = joinAltForeignAddressLines(contactData);
  const addressOut =
    addrType === 'B07002' && primaryForeignJoined ? primaryForeignJoined : trim(model.secretaryAddress);
  const alternateOut =
    (altType === 'B07002' && altForeignJoined) || (!altType && altForeignJoined)
      ? altForeignJoined
      : trim(model.secretaryAlternateAddress);

  return {
    entity: trim(entityCd),
    entityCd: trim(entityCd),
    entityName: { value: entityName },
    entityRegNo: { value: entityRegNo },
    companyName: entityName,
    companyRegistrationNo: entityRegNo,
    officerNameCd: contactCd,
    officerName,
    officerCode: contactCd,
    contactCd,
    position: trim(model.positionCd) || 'B30033',
    positionCd: trim(model.positionCd) || 'B30033',
    positionText: 'Company Secretary',
    appointedDate: appointmentDate,
    appointmentDate,
    appointmentEffectiveDate: appointmentDate,
    directorName: officerName,
    secretaryName: officerName,
    directorIdentificationNo: secId || trim(contactData?.contactRegNo),
    secretaryIdentificationNo: secId || trim(contactData?.contactRegNo),
    directorNationality: nationalityLabelForOutput(trim(model.secretaryNationality)) || trim(contactData?.nationalityText),
    secretaryNationality: nationalityLabelForOutput(trim(model.secretaryNationality)) || trim(contactData?.nationalityText),
    directorDateOfBirth: trim(model.secretaryDateOfBirth) || trim(contactData?.dateOfBirth),
    secretaryDateOfBirth: trim(model.secretaryDateOfBirth) || trim(contactData?.dateOfBirth),
    directorEmail: trim(model.secretaryEmail) || trim(contactData?.contactEmail),
    secretaryEmail: trim(model.secretaryEmail) || trim(contactData?.contactEmail),
    directorContactNo: trim(model.secretaryContactNo) || trim(contactData?.mobileNo),
    secretaryContactNo: trim(model.secretaryContactNo) || trim(contactData?.mobileNo),
    directorIdTypeCd: trim(model.secretaryIdTypeCd) || trim((contactData as any)?.ID_TYPE_CD || (contactData as any)?.idTypeCd),
    secretaryIdTypeCd: trim(model.secretaryIdTypeCd) || trim((contactData as any)?.ID_TYPE_CD || (contactData as any)?.idTypeCd),
    directorMobileCountryCd: trim(model.secretaryMobileCountryCd) || trim((contactData as any)?.COUNTRY_CD || (contactData as any)?.countryCd),
    secretaryMobileCountryCd: trim(model.secretaryMobileCountryCd) || trim((contactData as any)?.COUNTRY_CD || (contactData as any)?.countryCd),
    directorMobileAreaCode: trim(model.secretaryMobileAreaCode) || trim((contactData as any)?.AREA_CODE || (contactData as any)?.areaCode),
    secretaryMobileAreaCode: trim(model.secretaryMobileAreaCode) || trim((contactData as any)?.AREA_CODE || (contactData as any)?.areaCode),
    directorAddress: addressOut,
    secretaryAddress: addressOut,
    directorAlternateAddress: alternateOut,
    secretaryAlternateAddress: alternateOut,
    resolutionDate: trim(model.resolutionDate),
    executionDate: trim(model.executionDate),
    contactData,
  };
}

export function buildAppointSecretarySeedFormData(
  model: AppointSecretaryFormModel,
  entityCd: string,
): Record<string, any> {
  const ec = trim(entityCd);
  const fd = buildAppointSecretaryTaskFormData(model, ec);
  const templateVars = toAppointSecretaryTemplateVariables(model);
  const entityUEN = resolveEntityUenForFiling(model, ec);
  const filingAgentUEN = resolveFilingAgentUenForFiling();
  const contactCd = trim(fd.contactCd || fd.officerNameCd || '');

  let contactData = fd.contactData;
  if (!contactCd) {
    const built = buildOfficerAppointmentContactDataFromFlat({
      ...fd,
      ...templateVars,
    }) as Record<string, any>;
    if (trim((built as any).CONTACT_NAME)) {
      contactData = built;
    }
  }

  return enrichOfficerAppointmentSeedFormData({
    ...fd,
    ...templateVars,
    position: trim(model.positionCd) || 'B30033',
    positionCd: trim(model.positionCd) || 'B30033',
    contactData,
    appointedDate: { value: trim(fd.appointmentEffectiveDate || fd.appointedDate || '') },
    officerNameCd: contactCd,
    entityUEN,
    filingAgentUEN,
  });
}

export function isAppointSecretaryFormComplete(model: AppointSecretaryFormModel): boolean {
  const { name, id } = firstSecretaryNameId(model);
  return [
    model.companyName,
    model.companyRegistrationNo,
    model.resolutionDate,
    model.appointmentEffectiveDate,
    model.executionDate,
    model.secretaryAddress,
    model.secretaryNationality,
    model.secretaryDateOfBirth,
    model.secretaryEmail,
    model.secretaryContactNo,
    model.secretaryMobileCountryCd,
    name,
    id,
  ].every((v) => trim(v).length > 0);
}
